/**
 * ProductionSettingsPage — the "Console" orchestrator.
 *
 * Rebuilt from the former 2,666-line god component (4 tabs of ~40 collapsed
 * accordions) into a left rail of seven areas + a content panel:
 *
 * - Area registry + search index live in `../areas.ts`; panels under
 *   `../domains/*`; extracted sections under `../sections/*`.
 * - Chrome follows DESIGN.md: `usePageTitle` fills the AppLayout TopHeader
 *   (no rolled-own header), inventory-scale controls, brand-purple accent.
 * - One save model: sections register pending edits with
 *   SettingsDirtyContext; the sticky SettingsSaveBar saves/discards them all.
 * - Per-location areas bind to the global header LocationFilter selection
 *   (EditingLocationChip reflects it; no in-panel location picker).
 * - Legacy `?tab=` + hash deep-links from external surfaces resolve via
 *   `resolveLegacyLink` — the old hash-scroll machinery is gone.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { useRole } from '@/hooks/use-role';
import { getScrollContainer } from '@/lib/scroll-container';
import {
  DEFAULT_AREA,
  getArea,
  resolveLegacyLink,
  type SettingsAreaId,
} from '../areas';
import {
  SettingsDirtyProvider,
  useSettingsDirty,
} from './console/SettingsDirtyContext';
import { SettingsRail } from './console/SettingsRail';
import { SettingsSaveBar } from './console/SettingsSaveBar';
import { SettingsSearch, useSettingsSearchHotkey } from './console/SettingsSearch';
import { EditingLocationChip } from './console/EditingLocationChip';
import { scrollToSection } from './console/scrollToSection';
import { LocationsFulfilmentPanel } from '../domains/LocationsFulfilmentPanel';
import { OrdersRoutingPanel } from '../domains/OrdersRoutingPanel';
import { ProductionWorkflowPanel } from '../domains/ProductionWorkflowPanel';
import { PrintingFilesPanel } from '../domains/PrintingFilesPanel';
import { LabelsQrPanel } from '../domains/LabelsQrPanel';
import { IntegrationsAlertsPanel } from '../domains/IntegrationsAlertsPanel';
import { SystemDiagnosticsPanel } from '../domains/SystemDiagnosticsPanel';

const PANELS: Record<SettingsAreaId, () => JSX.Element> = {
  locations: LocationsFulfilmentPanel,
  orders: OrdersRoutingPanel,
  workflow: ProductionWorkflowPanel,
  printing: PrintingFilesPanel,
  'labels-qr': LabelsQrPanel,
  alerts: IntegrationsAlertsPanel,
  system: SystemDiagnosticsPanel,
};

export function ProductionSettingsPage() {
  usePageTitle('Production Settings', 'Configure the production floor');
  return (
    <SettingsDirtyProvider>
      <SettingsConsole />
    </SettingsDirtyProvider>
  );
}

function SettingsConsole() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin } = useRole();
  const { count: dirtyCount, discardAll } = useSettingsDirty();
  const [searchOpen, setSearchOpen] = useSettingsSearchHotkey();
  const [pendingArea, setPendingArea] = useState<SettingsAreaId | null>(null);
  const legacyHandled = useRef(false);

  const area = getArea(searchParams.get('area') ?? DEFAULT_AREA);

  const navigateToArea = useCallback(
    (next: SettingsAreaId, sectionId?: string | null) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('area', next);
        p.delete('tab');
        return p;
      });
      if (sectionId) {
        scrollToSection(sectionId);
      } else {
        const container = getScrollContainer(null);
        container?.scrollTo({ top: 0 });
      }
    },
    [setSearchParams],
  );

  // Deep-links: legacy `?tab=` + `#…-accordion` hashes (Issues gear, bridge
  // Slack alerts, Bridge Control, Split Orders gear) AND new-style
  // `#<section-id>` hashes (e.g. the shipping section's Configure… links).
  // Re-runs on every param/hash change; the tab param + hash are consumed on
  // resolution, so it settles immediately.
  useEffect(() => {
    const tab = searchParams.get('tab');
    const hash = window.location.hash;
    if (!tab && !hash) {
      legacyHandled.current = false;
      return;
    }
    if (legacyHandled.current) return;
    legacyHandled.current = true;
    const target = resolveLegacyLink(tab, hash);
    if (!target) return;
    if (hash && typeof window.history.replaceState === 'function') {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
    navigateToArea(target.area, target.section);
  }, [searchParams, navigateToArea]);

  // Guarded area switch: block rail navigation while edits are unsaved.
  const requestArea = useCallback(
    (next: SettingsAreaId) => {
      if (next === area.id) return;
      if (dirtyCount > 0) {
        setPendingArea(next);
        return;
      }
      navigateToArea(next);
    },
    [area.id, dirtyCount, navigateToArea],
  );

  // Alt+1..7 jumps between areas (successor of the old Alt+1-4 tab keys).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      const n = Number(e.key);
      if (!Number.isInteger(n) || n < 1 || n > 7) return;
      const target = ['locations', 'orders', 'workflow', 'printing', 'labels-qr', 'alerts', 'system'][
        n - 1
      ] as SettingsAreaId;
      e.preventDefault();
      requestArea(target);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestArea]);

  const Panel = PANELS[area.id];

  return (
    <div className="p-4 md:p-6 lg:p-8 animate-enter">
      <div className="flex flex-col gap-5 lg:flex-row lg:gap-8">
        {/* Rail stays pinned on lg+ while the settings content scrolls. */}
        <div className="lg:sticky lg:top-[calc(var(--top-header-height,3.5rem)+1rem)] lg:self-start lg:max-h-[calc(100dvh-var(--top-header-height,3.5rem)-2rem)] lg:overflow-y-auto">
          <SettingsRail
            activeArea={area.id}
            onSelect={requestArea}
            onOpenSearch={() => setSearchOpen(true)}
            isAdmin={isAdmin()}
          />
        </div>

        <div className="min-w-0 flex-1">
          {/* Sticky panel header: breadcrumb · area title · editing chip.
              Pins under TopHeader while the section cards scroll beneath it. */}
          <div
            // Negative top margin + matching padding extend the header's
            // backdrop up through the page's top padding, so scrolling
            // content can't peek through the strip under TopHeader.
            className="sticky z-30 -mx-1 -mt-4 md:-mt-6 lg:-mt-8 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border/60 bg-background/90 px-1 pb-2.5 pt-5 md:pt-7 lg:pt-9 backdrop-blur supports-[backdrop-filter]:bg-background/75"
            style={{ top: 'var(--top-header-height, 3.5rem)' }}
          >
            <div className="min-w-0">
              <nav aria-label="Breadcrumb" className="text-[11px] text-muted-foreground">
                Settings <span aria-hidden>›</span>{' '}
                <span className="font-medium text-foreground">{area.title}</span>
              </nav>
              <h2 className="truncate text-lg font-semibold leading-6 tracking-tight">
                {area.title}
              </h2>
            </div>
            {area.perLocation ? (
              <div className="ml-auto">
                <EditingLocationChip />
              </div>
            ) : null}
          </div>

          <p className="mt-3 mb-5 max-w-prose text-sm text-muted-foreground">{area.description}</p>

          {area.diagnostics ? (
            <p className="mb-4 rounded-md border border-dashed bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              These are live status surfaces and destructive actions, separated from configuration
              so they can’t be mistaken for settings.
            </p>
          ) : null}

          <div className="space-y-4 pb-2">
            <Panel />
          </div>

          <SettingsSaveBar />
        </div>
      </div>

      <SettingsSearch
        open={searchOpen}
        onOpenChange={setSearchOpen}
        isAdmin={isAdmin()}
        onJump={(areaId, sectionId) => {
          if (dirtyCount > 0 && areaId !== area.id) {
            // Jumping areas would drop unsaved edits — surface the guard.
            setPendingArea(areaId);
            return;
          }
          navigateToArea(areaId, sectionId);
        }}
      />

      {/* Unsaved-changes guard for area switches. */}
      <AlertDialog open={pendingArea !== null} onOpenChange={(open) => !open && setPendingArea(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have {dirtyCount === 1 ? 'an unsaved change' : `${dirtyCount} unsaved changes`} in{' '}
              {area.title}. Leaving this area will discard {dirtyCount === 1 ? 'it' : 'them'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const next = pendingArea;
                setPendingArea(null);
                if (next) {
                  discardAll();
                  navigateToArea(next);
                }
              }}
            >
              Discard and leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
